import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { SmartIrrigationRootNodeDto } from 'src/api/models/smart-irrigation-root-node-dto';
import { SmartIrrigationDeviceDto } from 'src/api/models/smart-irrigation-device-dto';
import { SmartIrrigationDeviceTypeDto } from 'src/api/models/smart-irrigation-device-type-dto';
import { SmartIrrigationNodeUserDto } from 'src/api/models/smart-irrigation-node-user-dto';
import { GeoAssetDetailDTO, User } from 'src/api/models';
import { SmartIrrigationService } from 'src/api/services';
import { ToastrService } from 'ngx-toastr';
import { NodeDragService } from './node-drag.service';
import {
  faChevronRight, faChevronDown, faPencilAlt, faPlus, faTrash, faCheck, faTimes,
  faCog, faLink, faUser, faArrowsAlt
} from '@fortawesome/free-solid-svg-icons';

export interface NodeTreeChangeEvent {
  type: 'delete-node' | 'move-node';
  nodeId: number;
  newParentNodeId?: number;
}

@Component({
  selector: 'app-node-tree-item',
  templateUrl: './node-tree-item.component.html',
  styleUrls: ['./node-tree-item.component.scss']
})
export class NodeTreeItemComponent {

  @Input() node!: SmartIrrigationRootNodeDto;
  @Input() customerId!: number;
  @Input() isRoot: boolean = false;
  @Input() level: number = 0;
  @Input() geoAssets: GeoAssetDetailDTO[] = [];

  @Output() nodeChanged = new EventEmitter<NodeTreeChangeEvent>();

  @ViewChild('editInputEl') editInputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('addInputEl') addInputEl?: ElementRef<HTMLInputElement>;

  // Icons
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faPencilAlt = faPencilAlt;
  faPlus = faPlus;
  faTrash = faTrash;
  faCheck = faCheck;
  faTimes = faTimes;
  faCog = faCog;
  faLink = faLink;
  faUser = faUser;
  faArrowsAlt = faArrowsAlt;

  // Tree state
  isExpanded = true;
  isEditing = false;
  isAddingChild = false;
  editName = '';
  newChildName = '';
  isSaving = false;

  // Drag & drop state
  isDragOver = false;
  isMoving = false;

  // Details panel state
  isDetailsOpen = false;
  selectedGeoAssetId: number | null = null;
  isSavingDetails = false;

  // Device linking state
  isLinkingDevice = false;
  isLoadingDevices = false;
  unlinkedDevices: SmartIrrigationDeviceDto[] = [];
  selectedDeviceId: number | null = null;

  // Add new device state
  isAddingNewDevice = false;
  deviceTypes: SmartIrrigationDeviceTypeDto[] = [];
  selectedDeviceTypeId: number | null = null;
  newDeviceUri = '';

  // Water allowance state
  editWaterAllowance: number | null = null;
  editAllowanceResetMonth: number | null = null;
  isSavingWaterAllowance = false;

  readonly months = [
    { value: 0, label: 'January' }, { value: 1, label: 'February' }, { value: 2, label: 'March' },
    { value: 3, label: 'April' }, { value: 4, label: 'May' }, { value: 5, label: 'June' },
    { value: 6, label: 'July' }, { value: 7, label: 'August' }, { value: 8, label: 'September' },
    { value: 9, label: 'October' }, { value: 10, label: 'November' }, { value: 11, label: 'December' }
  ];

  // User linking state
  isLinkingUser = false;
  isLoadingUsers = false;
  associatableUsers: User[] = [];
  selectedUserId: number | null = null;

  constructor(
    private irrigation: SmartIrrigationService,
    private toastr: ToastrService,
    private dragService: NodeDragService
  ) {}

  get hasChildren(): boolean {
    return !!(this.node.childNodes && this.node.childNodes.length > 0);
  }

  // Visual-only prefix indicating the node's position in the Region/Park/Ground hierarchy.
  get levelPrefix(): string {
    switch (this.level) {
      case 1: return 'Region: ';
      case 2: return 'Park: ';
      case 3: return 'Ground: ';
      default: return '';
    }
  }

  get hasWaterMeter(): boolean {
    return !!(this.node.associatedDevices?.some(d =>
      d.deviceTypeName?.toLowerCase().includes('water meter')
    ));
  }

  get waterAllowanceWithoutMeter(): boolean {
    return (this.node.annualWaterAllowance != null) && !this.hasWaterMeter;
  }

  get hasLinkedAssociations(): boolean {
    return !!((this.node.associatedDevices && this.node.associatedDevices.length > 0)
      || (this.node.associatedUsers && this.node.associatedUsers.length > 0));
  }

  get deleteDisabledReason(): string {
    return this.hasLinkedAssociations
      ? 'Delete is disabled while devices or users are linked to this node.'
      : 'Delete node';
  }

  // ── Details panel ─────────────────────────────────────────────────────────

  toggleDetails(): void {
    this.isDetailsOpen = !this.isDetailsOpen;
    if (this.isDetailsOpen) {
      this.selectedGeoAssetId = this.node.geoAssetId ?? null;
      this.editWaterAllowance = this.node.annualWaterAllowance ?? null;
      this.editAllowanceResetMonth = this.node.annualWaterAllowanceResetMonth ?? null;
      this.isLinkingDevice = false;
      this.unlinkedDevices = [];
      this.selectedDeviceId = null;
      this.isLinkingUser = false;
      this.associatableUsers = [];
      this.selectedUserId = null;
    }
  }

  saveGeoAsset(): void {
    if (this.isSavingDetails) { return; }
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationUpdateNode({
      customerId: this.customerId,
      node: { 
        id: this.node.id, 
        name: this.node.name, 
        geoAssetId: this.selectedGeoAssetId ?? undefined,
        parentNodeId: this.node.parentNodeId,
        annualWaterAllowance: this.node.annualWaterAllowance ?? undefined,
        annualWaterAllowanceResetMonth: this.node.annualWaterAllowanceResetMonth ?? undefined
      }
    }).subscribe({
      next: () => {
        this.isSavingDetails = false;
        this.node.geoAssetId = this.selectedGeoAssetId ?? undefined;
        this.toastr.success('Associated asset saved.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to save associated asset.');
      }
    });
  }

  saveWaterAllowance(): void {
    if (this.isSavingWaterAllowance) { return; }
    const allowance = this.editWaterAllowance;
    const month = this.editAllowanceResetMonth;
    if (allowance !== null && (allowance < 0 || allowance > 100000)) { return; }
    this.isSavingWaterAllowance = true;
    this.irrigation.SmartIrrigationUpdateNode({
      customerId: this.customerId,
      node: {
        id: this.node.id,
        name: this.node.name,
        geoAssetId: this.node.geoAssetId ?? undefined,
        parentNodeId: this.node.parentNodeId,
        annualWaterAllowance: allowance ?? undefined,
        annualWaterAllowanceResetMonth: month ?? undefined
      }
    }).subscribe({
      next: (updated) => {
        this.isSavingWaterAllowance = false;
        this.node.annualWaterAllowance = updated.annualWaterAllowance;
        this.node.annualWaterAllowanceResetMonth = updated.annualWaterAllowanceResetMonth;
        this.editWaterAllowance = updated.annualWaterAllowance ?? null;
        this.editAllowanceResetMonth = updated.annualWaterAllowanceResetMonth ?? null;
        this.toastr.success('Water allowance saved.');
      },
      error: () => {
        this.isSavingWaterAllowance = false;
        this.toastr.error('Failed to save water allowance.');
      }
    });
  }

  startLinkDevice(): void {
    this.isLinkingDevice = true;
    this.isAddingNewDevice = false;
    this.selectedDeviceId = null;
    this.isLoadingDevices = true;
    Promise.all([
      this.irrigation.SmartIrrigationGetUnlinkedDevices(this.customerId).toPromise(),
      this.deviceTypes.length ? Promise.resolve(this.deviceTypes) : this.irrigation.SmartIrrigationGetDeviceTypes().toPromise()
    ]).then(([devices, types]) => {
      this.unlinkedDevices = devices || [];
      this.deviceTypes = types || [];
      this.isLoadingDevices = false;
    }).catch(() => {
      this.isLoadingDevices = false;
      this.toastr.error('Failed to load available devices.');
    });
  }

  cancelLinkDevice(): void {
    this.isLinkingDevice = false;
    this.isAddingNewDevice = false;
    this.selectedDeviceId = null;
    this.newDeviceUri = '';
    this.selectedDeviceTypeId = null;
    this.unlinkedDevices = [];
  }

  startAddNewDevice(): void {
    this.isAddingNewDevice = true;
    this.selectedDeviceId = null;
    this.newDeviceUri = '';
    this.selectedDeviceTypeId = null;
  }

  cancelAddNewDevice(): void {
    this.isAddingNewDevice = false;
    this.selectedDeviceTypeId = null;
    this.newDeviceUri = '';
  }

  addNewDevice(): void {
    if (!this.selectedDeviceTypeId || this.isSavingDetails) { return; }
    const deviceTypeId = this.selectedDeviceTypeId;
    const uri = this.newDeviceUri.trim();
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationAddNodeDevice({
      customerId: this.customerId,
      device: { associatedNodeId: this.node.id, deviceTypeId, uri: uri || undefined }
    }).subscribe({
      next: (created) => {
        this.isSavingDetails = false;
        this.isLinkingDevice = false;
        this.isAddingNewDevice = false;
        this.selectedDeviceTypeId = null;
        this.newDeviceUri = '';
        this.node.associatedDevices = [...(this.node.associatedDevices || []), created];
        this.toastr.success('Device added successfully.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to add device.');
      }
    });
  }

  linkDevice(): void {
    if (!this.selectedDeviceId || this.isSavingDetails) { return; }
    const selectedDeviceId = this.selectedDeviceId;
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationLinkDeviceToNode({
      customerId: this.customerId,
      link: { associatedNodeId: this.node.id, deviceId: selectedDeviceId }
    }).subscribe({
      next: () => {
        this.isSavingDetails = false;
        this.isLinkingDevice = false;
        this.selectedDeviceId = null;
        const linked = this.unlinkedDevices.find(d => d.id === selectedDeviceId);
        if (linked) {
          this.node.associatedDevices = [...(this.node.associatedDevices || []), linked];
        }
        this.toastr.success('Device linked successfully.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to link device.');
      }
    });
  }

  unlinkDevice(device: SmartIrrigationDeviceDto): void {
    if (!window.confirm(`Unlink "${device.deviceTypeName || device.uri}"? This cannot be undone.`)) { return; }
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationDeleteNodeDevice({
      customerId: this.customerId,
      deviceId: device.id
    }).subscribe({
      next: () => {
        this.isSavingDetails = false;
        this.node.associatedDevices = (this.node.associatedDevices || []).filter(d => d.id !== device.id);
        this.toastr.success('Device unlinked successfully.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to unlink device.');
      }
    });
  }

  startLinkUser(): void {
    this.isLinkingUser = true;
    this.selectedUserId = null;
    this.isLoadingUsers = true;
    this.irrigation.SmartIrrigationGetAssociatableUsers(this.customerId).subscribe({
      next: (users) => {
        this.associatableUsers = users;
        this.isLoadingUsers = false;
      },
      error: () => {
        this.isLoadingUsers = false;
        this.toastr.error('Failed to load users.');
      }
    });
  }

  cancelLinkUser(): void {
    this.isLinkingUser = false;
    this.selectedUserId = null;
    this.associatableUsers = [];
  }

  linkUser(): void {
    if (!this.selectedUserId || this.isSavingDetails) { return; }
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationAddNodeUser({
      customerId: this.customerId,
      nodeUser: { nodeId: this.node.id, userId: this.selectedUserId, roleId: 1 }
    }).subscribe({
      next: () => {
        this.isSavingDetails = false;
        this.isLinkingUser = false;
        const selectedUserId = this.selectedUserId;
        this.selectedUserId = null;
        const linkedUser = this.associatableUsers.find(u => u.userId === selectedUserId);
        if (linkedUser) {
          this.node.associatedUsers = [
            ...(this.node.associatedUsers || []),
            { id: selectedUserId, displayName: linkedUser.displayName || `${linkedUser.firstName || ''} ${linkedUser.lastName || ''}`.trim(), email: linkedUser.email }
          ];
        }
        this.toastr.success('User linked successfully.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to link user.');
      }
    });
  }

  unlinkUser(nodeUser: SmartIrrigationNodeUserDto): void {
    if (!window.confirm(`Unlink "${nodeUser.userDisplayName || 'this user'}"? This cannot be undone.`)) { return; }
    this.isSavingDetails = true;
    this.irrigation.SmartIrrigationDeleteNodeUser({
      customerId: this.customerId,
      nodeUserId: nodeUser.id
    }).subscribe({
      next: () => {
        this.isSavingDetails = false;
        this.node.associatedUsers = (this.node.associatedUsers || []).filter(u => u.id !== nodeUser.id);
        this.toastr.success('User unlinked successfully.');
      },
      error: () => {
        this.isSavingDetails = false;
        this.toastr.error('Failed to unlink user.');
      }
    });
  }

  // ── Node tree operations ──────────────────────────────────────────────────

  startEdit(): void {
    this.editName = this.node.name || '';
    this.isEditing = true;
    this.isAddingChild = false;
    setTimeout(() => this.editInputEl?.nativeElement?.focus(), 0);
  }

  cancelEdit(): void {
    this.isEditing = false;
  }

  saveEdit(): void {
    if (!this.editName.trim() || this.isSaving) { return; }
    this.isSaving = true;
    this.irrigation.SmartIrrigationUpdateNode({
      customerId: this.customerId,
      node: {
        id: this.node.id,
        name: this.editName.trim(),
        geoAssetId: this.node.geoAssetId ?? undefined,
        parentNodeId: this.node.parentNodeId,
        annualWaterAllowance: this.node.annualWaterAllowance ?? undefined,
        annualWaterAllowanceResetMonth: this.node.annualWaterAllowanceResetMonth ?? undefined
      }
    }).subscribe({
      next: () => {
        this.isSaving = false;
        this.isEditing = false;
        this.node.name = this.editName.trim();
        this.toastr.success('Node renamed successfully.');
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error('Failed to rename node. Please try again.');
      }
    });
  }

  startAddChild(): void {
    this.newChildName = '';
    this.isAddingChild = true;
    this.isEditing = false;
    this.isExpanded = true;
    setTimeout(() => this.addInputEl?.nativeElement?.focus(), 0);
  }

  cancelAddChild(): void {
    this.isAddingChild = false;
  }

  saveAddChild(): void {
    if (!this.newChildName.trim() || this.isSaving) { return; }
    this.isSaving = true;
    this.irrigation.SmartIrrigationAddNode({
      customerId: this.customerId,
      node: { name: this.newChildName.trim(), parentNodeId: this.node.id }
    }).subscribe({
      next: (created) => {
        this.isSaving = false;
        this.isAddingChild = false;
        const child = {
          id: created.id,
          name: created.name || this.newChildName.trim(),
          parentNodeId: created.parentNodeId ?? this.node.id,
          createdDate: created.createdDate,
          updatedDate: created.updatedDate,
          childNodes: [],
          associatedDevices: [],
          associatedUsers: []
        } as SmartIrrigationRootNodeDto;
        this.node.childNodes = [...(this.node.childNodes || []), child];
        this.toastr.success('Child node added successfully.');
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error('Failed to add child node. Please try again.');
      }
    });
  }

  deleteNode(): void {
    const hasKids = this.hasChildren;
    const msg = hasKids
      ? `Delete "${this.node.name}" and all its child nodes? This cannot be undone.`
      : `Delete "${this.node.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) { return; }

    this.isSaving = true;
    this.irrigation.SmartIrrigationDeleteNode({
      customerId: this.customerId,
      nodeId: this.node.id
    }).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastr.success('Node deleted successfully.');
        this.nodeChanged.emit({ type: 'delete-node', nodeId: this.node.id });
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error('Failed to delete node. Please try again.');
      }
    });
  }

  onChildChanged(change: NodeTreeChangeEvent): void {
    if (change.type === 'delete-node') {
      const before = this.node.childNodes?.length || 0;
      this.node.childNodes = (this.node.childNodes || []).filter(c => c.id !== change.nodeId);
      const after = this.node.childNodes.length;
      if (before !== after) {
        return;
      }
    }
    this.nodeChanged.emit(change);
  }

  // ── Drag & drop (move node) ───────────────────────────────────────────────

  onDragStart(event: DragEvent): void {
    this.dragService.draggedNode = this.node;
    event.dataTransfer?.setData('text/plain', String(this.node.id));
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragEnd(): void {
    this.dragService.draggedNode = null;
    this.isDragOver = false;
  }

  onDragOver(event: DragEvent): void {
    const dragged = this.dragService.draggedNode;
    if (!dragged || !this.isValidDropTarget(dragged)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const dragged = this.dragService.draggedNode;
    this.dragService.draggedNode = null;
    if (!dragged || !this.isValidDropTarget(dragged) || this.isMoving) {
      return;
    }

    this.isMoving = true;
    this.irrigation.SmartIrrigationUpdateNode({
      customerId: this.customerId,
      node: {
        id: dragged.id,
        name: dragged.name,
        geoAssetId: dragged.geoAssetId ?? undefined,
        parentNodeId: this.node.id,
        annualWaterAllowance: dragged.annualWaterAllowance ?? undefined,
        annualWaterAllowanceResetMonth: dragged.annualWaterAllowanceResetMonth ?? undefined
      }
    }).subscribe({
      next: () => {
        this.isMoving = false;
        this.toastr.success(`Moved "${dragged.name}" to "${this.node.name}".`);
        this.nodeChanged.emit({ type: 'move-node', nodeId: dragged.id, newParentNodeId: this.node.id });
      },
      error: () => {
        this.isMoving = false;
        this.toastr.error('Failed to move node. Please try again.');
      }
    });
  }

  private isValidDropTarget(dragged: SmartIrrigationRootNodeDto): boolean {
    if (dragged.id === this.node.id) { return false; }
    if (dragged.parentNodeId === this.node.id) { return false; }
    return !this.isDescendantOf(dragged, this.node.id);
  }

  private isDescendantOf(node: SmartIrrigationRootNodeDto, candidateId: number): boolean {
    return (node.childNodes || []).some(child => child.id === candidateId || this.isDescendantOf(child, candidateId));
  }
}

